import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Dialog from "../../../../../../../common/element/Dialog";
import BtnPrimary from "../../../../../../../common/element/Buttons/btnPrimary";
import ThreeDotLoader from "../../../../../../../common/element/ButtonLoader/ThreeDotLoader";
import SweetAlertService from "../../../../../../../common/SweetAlert/SweetAlert";

import {
  addOrUpdateCompanyQuota,
  clearAddUpdateQuotaState,
} from "../../../../../../../redux/InterViewCandidate/interViewCandidateSlice";

import { selectAddUpdateQuotaState } from "../../../../../../../redux/InterViewCandidate/interViewSelector";

const AddQuotaModal = ({
  dialogOpen,
  onDialogClose,
  onSubmit,
  candidateList,
  quota,
}) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Determine if we are Adding or Editing
  // Note: Added safe navigation (?.) to prevent crashes if quota is undefined
  const isEditMode = candidateList?.length > 0 || (quota?.total_quota && quota?.total_quota !== 0);
  const AddEditLogic = isEditMode ? "Edit" : "Add";

  const companyId = searchParams.get("companyId");

  const { loading, success, error, data } = useSelector(
    selectAddUpdateQuotaState
  );

  const [formData, setFormData] = useState({
    quota: "",
  });

  const [errors, setErrors] = useState({
    quota: "",
  });

  // Get used quota safely (default to 0)
  const usedQuota = quota?.used_quota || 0;

  // ✅ Reset form & Pre-fill data when modal opens
  useEffect(() => {
    if (dialogOpen) {
      // If editing, prefill with total_quota, otherwise empty
      setFormData({ 
        quota: quota?.total_quota ? String(quota.total_quota) : "" 
      });
      setErrors({ quota: "" });
      dispatch(clearAddUpdateQuotaState());
    }
  }, [dialogOpen, dispatch, quota]);

  // Success handler
  useEffect(() => {
    if (success && data) {
      SweetAlertService.showSuccess({
        title: data?.message,
      });

      if (onSubmit) {
        onSubmit(formData);
      }

      onDialogClose();
      dispatch(clearAddUpdateQuotaState());
    }
  }, [success, data, dispatch, onDialogClose, formData, onSubmit]);

  // Error handler
  useEffect(() => {
    if (error) {
      SweetAlertService.showError({
        title: "Error!",
        message: error || "Failed to add/update quota. Please try again.",
      });

      dispatch(clearAddUpdateQuotaState());
    }
  }, [error, dispatch]);

  // ✅ Validation Logic (Used in handleChange and handleSubmit)
  const validateInput = (value) => {
    const numValue = Number(value);

    if (value === "" || value.trim() === "") {
      return "Quota is required";
    }
    
    if (numValue <= 0) {
      return "Quota must be greater than 0";
    }

    // ✅ New Check: Prevent updating quota to less than what is already used
    if (numValue < usedQuota) {
      return `You have already used ${usedQuota} quota. You cannot update it to ${numValue}.`;
    }

    return ""; // No error
  };

  // ✅ Handle Change with Real-time Validation
  const handleChange = (field, value) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Run validation immediately on change
    const errorMsg = validateInput(value);
    setErrors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  // ✅ Submit handler
  const handleSubmit = async () => {
    // Run final validation before dispatching
    const errorMsg = validateInput(formData.quota);
    
    if (errorMsg) {
      setErrors({ quota: errorMsg });
      return;
    }

    if (!companyId) {
      SweetAlertService.showError({
        title: "Error!",
        message: "Company ID is missing. Please try again.",
      });
      return;
    }

    const payload = {
      total_quota: Number(formData.quota),
      company_id: companyId,
    };

    try {
      await dispatch(addOrUpdateCompanyQuota(payload));
    } catch (err) {
      console.error("Dispatch error:", err);
    }
  };

  const handleClose = () => {
    dispatch(clearAddUpdateQuotaState());
    onDialogClose();
  };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={handleClose}
      width={600}
      onRequestClose={handleClose}
      bodyOpenClassName="overflow-hidden"
    >
      {/* Header */}
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.527507 12.4774L2.22228 12.0229C2.60049 11.9216 2.99287 12.148 3.09408 12.5262L4.7356 18.6504C4.83681 19.0286 4.61043 19.421 4.23224 19.5222L2.53747 19.9759C2.15926 20.0771 1.76687 19.8507 1.66567 19.4725L0.0241423 13.3493C-0.0770647 12.9711 0.14932 12.5786 0.527507 12.4774ZM12.0946 5.36418H17.3778C17.3778 5.33755 17.3787 5.31092 17.3787 5.28339C17.3787 2.36509 15.0127 0 12.0955 0L12.0946 5.36418ZM16.4732 6.18897C16.4732 9.10726 14.1073 11.4724 11.19 11.4724C8.27184 11.4724 5.90683 9.10636 5.90683 6.18897C5.90683 3.27067 8.27275 0.905575 11.19 0.905575V6.18897H16.4732ZM13.5462 16.5294C13.8587 15.9576 13.7956 15.1888 13.0579 14.7626L8.7797 12.2928C8.35888 12.0495 7.90968 11.9909 7.44004 12.1161L3.8464 13.0794L5.10972 17.7955L6.33931 17.4661C6.80895 17.34 7.24929 17.3684 7.65413 17.5504L11.3403 19.2098C11.8143 19.4228 12.3311 19.4566 12.8815 19.3092L18.8056 17.7218C19.6214 17.5034 20.1435 16.7789 19.965 16.1122C19.7866 15.4454 18.9725 15.0796 18.1566 15.298L14.2273 16.3466C14.1642 16.5579 14.087 16.7532 14.0497 16.8162C13.5952 17.6037 12.7012 17.855 11.9022 17.4128C10.8404 16.834 9.77503 16.185 8.72399 15.5786C8.58461 15.4978 8.53755 15.3203 8.61834 15.1809C8.69913 15.0415 8.87668 14.9944 9.01607 15.0752C10.0708 15.6843 11.1317 16.288 12.1829 16.9041C12.7013 17.1918 13.25 17.0417 13.5465 16.5295L13.5462 16.5294Z"
                fill="#344054"
              />
            </svg>
          </span>
          <h5 className="mb-0 text-gray-900 text-base font-semibold">
            {AddEditLogic} Quota
          </h5>
        </div>

        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
          disabled={loading}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="px-6 pb-5">
        <div className="flex items-start gap-4">
          <label className="w-20 text-sm font-medium text-gray-700 pt-2">
            Quota
          </label>

          <div className="flex-1">
            <input
              className={`h-9 pl-3 w-full input input-shadow text-sm border ${
                errors.quota ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-gray-400`}
              value={formData.quota}
              placeholder="Enter quota number"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => handleChange("quota", e.target.value)}
              disabled={loading}
            />
            {/* Helper text showing used quota */}
            <p className="text-xs text-gray-500 mt-1">
               Currently Used: {usedQuota}
            </p>
            
            {errors.quota && (
              <p className="text-xs text-red-600 mt-1">{errors.quota}</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex dailog-footer px-6 pb-6 gap-3">
        <button
          className="whiteBtnShadow btn border border-gray-100 w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
          onClick={handleClose}
          disabled={loading}
          style={{ backgroundColor: "#FFF4E5" }}
          type="button"
        >
          Cancel
        </button>

        <BtnPrimary
          disabled={loading} // Button is disabled if loading, you can also add || errors.quota !== "" to disable on error
          onClick={handleSubmit}
          className="w-full"
          type="button"
          style={{
            backgroundColor: "#FF8E1C",
            borderColor: "#FF8E1C",
          }}
        >
          {loading ? <ThreeDotLoader /> : `${AddEditLogic} Quota`}
        </BtnPrimary>
      </div>
    </Dialog>
  );
};

export default AddQuotaModal;